import { useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import HandshakeIcon from "@mui/icons-material/Handshake";
import PublishIcon from "@mui/icons-material/Publish";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listExerciseModes, listSlotTemplates } from "@/api/catalog";
import { listOrganizations } from "@/api/organizations";
import {
  acceptApplication,
  applyToOpenShift,
  cancelOpenShift,
  createOpenShift,
  deleteOpenShift,
  listApplications,
  listEligiblePractitioners,
  listMyApplications,
  listOpenShifts,
  publishOpenShift,
  rejectApplication,
  withdrawApplication,
} from "@/api/openShifts";
import { listPractitioners } from "@/api/practitioners";
import type {
  ExerciseMode,
  OpenShift,
  OpenShiftApplication,
  Practitioner,
  SlotTemplate,
} from "@/api/types";
import { usePermission } from "@/auth/usePermission";
import { PageHeader } from "@/components/PageHeader";
import { SwapPanel } from "@/components/SwapPanel";
import { useAuthStore } from "@/store/authStore";

const SHIFT_STATUS_COLOR: Record<string, "default" | "primary" | "success" | "warning" | "error"> = {
  DRAFT: "default",
  PUBLISHED: "primary",
  FILLED: "success",
  CLOSED: "default",
  CANCELLED: "error",
};

const APPLICATION_STATUS_COLOR: Record<string, "default" | "primary" | "success" | "warning" | "error"> = {
  PROPOSED: "primary",
  ACCEPTED: "success",
  REJECTED: "error",
  WITHDRAWN: "default",
};


export function ExchangePage() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.me);
  const canManage = usePermission("open_shift:write");
  const canApply = usePermission("open_shift_application:write_self");

  // Persistent tab — default to "manage" if coord, "available" if doctor.
  type TabKey = "available" | "myApplications" | "manage" | "swaps";
  const [tab, setTab] = useState<TabKey>(canManage ? "manage" : "available");

  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 100 }),
  });
  const [orgId, setOrgId] = useState<string>("");
  useEffect(() => {
    if (!orgId && orgs.data?.items.length) {
      const preferred = me?.active_org_id && orgs.data.items.find((o) => o.id === me.active_org_id)
        ? me.active_org_id
        : orgs.data.items[0]?.id ?? "";
      setOrgId(preferred);
    }
  }, [orgs.data, orgId, me?.active_org_id]);

  if (orgs.isLoading) return <CircularProgress />;
  if ((orgs.data?.items.length ?? 0) === 0) {
    return (
      <Box>
        <PageHeader title={t("exchange.title")} subtitle={t("exchange.subtitle")} helpTerm="bourse" />
        <Alert severity="info">{t("exchange.noOrgAccessible")}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title={t("exchange.title")} subtitle={t("exchange.subtitle")} />

      <FormControl size="small" sx={{ minWidth: 280, mb: 2 }}>
        <InputLabel>{t("exchange.selectOrganization")}</InputLabel>
        <Select
          label={t("exchange.selectOrganization")}
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
        >
          {(orgs.data?.items ?? []).map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.code} — {o.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v as TabKey)} variant="scrollable">
          <Tab value="available" label={t("exchange.tabAvailable")} />
          {canApply && <Tab value="myApplications" label={t("exchange.tabMyApplications")} />}
          {canApply && <Tab value="swaps" label={t("exchange.tabSwaps")} />}
          {canManage && <Tab value="manage" label={t("exchange.tabManage")} />}
        </Tabs>
      </Box>

      {tab === "available" && <AvailableTab orgId={orgId} canApply={canApply} />}
      {tab === "myApplications" && canApply && <MyApplicationsTab />}
      {tab === "swaps" && canApply && <SwapPanel />}
      {tab === "manage" && canManage && <ManageTab orgId={orgId} />}
    </Box>
  );
}


// ---------------------------------------------------------------------------
// Available tab (practitioner view)
// ---------------------------------------------------------------------------
function AvailableTab({ orgId, canApply }: { orgId: string; canApply: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const shifts = useQuery({
    queryKey: ["open-shifts", orgId, "PUBLISHED"],
    queryFn: () => (orgId ? listOpenShifts({ organization_id: orgId, status: "PUBLISHED" }) : Promise.resolve([])),
    enabled: Boolean(orgId),
  });

  const myApplications = useQuery({
    queryKey: ["my-open-shift-applications"],
    queryFn: () => listMyApplications(),
    enabled: canApply,
  });

  const slots = useQuery({
    queryKey: ["slot-templates", orgId, true],
    queryFn: () => (orgId ? listSlotTemplates(orgId, true) : Promise.resolve([])),
    enabled: Boolean(orgId),
  });
  const modes = useQuery({
    queryKey: ["exercise-modes-active"],
    queryFn: () => listExerciseModes(true),
  });

  const slotById = useMemo(() => {
    const m = new Map<string, SlotTemplate>();
    for (const s of slots.data ?? []) m.set(s.id, s);
    return m;
  }, [slots.data]);
  const modeById = useMemo(() => {
    const m = new Map<string, ExerciseMode>();
    for (const e of modes.data ?? []) m.set(e.id, e);
    return m;
  }, [modes.data]);

  const appliedShiftIds = new Set(
    (myApplications.data ?? [])
      .filter((a) => a.status === "PROPOSED" || a.status === "ACCEPTED")
      .map((a) => a.open_shift_id),
  );

  const [applying, setApplying] = useState<OpenShift | null>(null);
  const [coverText, setCoverText] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  const applyMutation = useMutation({
    mutationFn: () => applyToOpenShift(applying!.id, coverText || undefined),
    onSuccess: () => {
      setApplying(null);
      setCoverText("");
      setServerError(null);
      qc.invalidateQueries({ queryKey: ["my-open-shift-applications"] });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      if (detail === "already_applied") setServerError(t("exchange.alreadyApplied"));
      else if (detail === "shift_full") setServerError(t("exchange.shiftFull"));
      else if (detail === "shift_not_open_for_application") setServerError(t("exchange.shiftNotOpen"));
      else if (detail === "user_not_a_practitioner") setServerError(t("exchange.needPractitionerLink"));
      else setServerError(typeof detail === "string" ? detail : (e as Error).message);
    },
  });

  if (shifts.isLoading) return <CircularProgress />;
  if ((shifts.data ?? []).length === 0) {
    return <Alert severity="info">{t("exchange.noShifts")}</Alert>;
  }

  return (
    <>
      <Stack spacing={1.5}>
        {(shifts.data ?? []).map((s) => (
          <ShiftCard
            key={s.id}
            shift={s}
            slot={slotById.get(s.slot_template_id)}
            mode={modeById.get(s.exercise_mode_id)}
            isMine={appliedShiftIds.has(s.id)}
            action={
              canApply && !appliedShiftIds.has(s.id) && s.places_taken < s.total_places ? (
                <Button
                  variant="contained"
                  startIcon={<HandshakeIcon />}
                  onClick={() => {
                    setApplying(s);
                    setCoverText("");
                    setServerError(null);
                  }}
                >
                  {t("exchange.apply")}
                </Button>
              ) : appliedShiftIds.has(s.id) ? (
                <Chip color="success" label={t("exchange.applicationStatus.PROPOSED")} />
              ) : null
            }
          />
        ))}
      </Stack>

      <Dialog open={applying !== null} onClose={() => setApplying(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t("exchange.apply")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {serverError && <Alert severity="error">{serverError}</Alert>}
            {applying && (
              <Typography variant="body2" color="text.secondary">
                {applying.date} · {slotById.get(applying.slot_template_id)?.label}
              </Typography>
            )}
            <TextField
              label={t("exchange.applyCover")}
              value={coverText}
              onChange={(e) => setCoverText(e.target.value)}
              multiline
              minRows={2}
              maxRows={5}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplying(null)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={() => applyMutation.mutate()}
            disabled={applyMutation.isPending}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


// ---------------------------------------------------------------------------
// My applications tab
// ---------------------------------------------------------------------------
function MyApplicationsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const applications = useQuery({
    queryKey: ["my-open-shift-applications"],
    queryFn: () => listMyApplications(),
  });
  const shifts = useQuery({
    queryKey: ["open-shifts-all-status"],
    queryFn: () => listOpenShifts({}),
  });

  const withdrawMutation = useMutation({
    mutationFn: ({ shiftId, applicationId }: { shiftId: string; applicationId: string }) =>
      withdrawApplication(shiftId, applicationId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-open-shift-applications"] }),
  });

  if (applications.isLoading) return <CircularProgress />;
  if ((applications.data ?? []).length === 0) {
    return <Alert severity="info">{t("exchange.noMyApplications")}</Alert>;
  }

  const shiftById = new Map<string, OpenShift>();
  for (const s of shifts.data ?? []) shiftById.set(s.id, s);

  return (
    <List>
      {(applications.data ?? []).map((a) => {
        const shift = shiftById.get(a.open_shift_id);
        return (
          <ListItem
            key={a.id}
            divider
            secondaryAction={
              a.status === "PROPOSED" && (
                <Tooltip title={t("exchange.withdrawConfirm")}>
                  <IconButton
                    edge="end"
                    color="error"
                    onClick={() => {
                      if (confirm(t("exchange.withdrawConfirm"))) {
                        withdrawMutation.mutate({
                          shiftId: a.open_shift_id,
                          applicationId: a.id,
                        });
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )
            }
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 2 }}>
              <Chip
                size="small"
                color={APPLICATION_STATUS_COLOR[a.status]}
                label={t(`exchange.applicationStatus.${a.status}`)}
              />
            </Stack>
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {shift?.date ?? a.open_shift_id.slice(0, 8)}
                  </Typography>
                  {shift && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t(`exchange.shiftStatus.${shift.status}`)}
                    />
                  )}
                </Stack>
              }
              secondary={a.cover_text ?? a.decision_reason ?? undefined}
            />
          </ListItem>
        );
      })}
    </List>
  );
}


// ---------------------------------------------------------------------------
// Manage tab (coord/admin)
// ---------------------------------------------------------------------------
function ManageTab({ orgId }: { orgId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const shifts = useQuery({
    queryKey: ["open-shifts-mgmt", orgId],
    queryFn: () => (orgId ? listOpenShifts({ organization_id: orgId }) : Promise.resolve([])),
    enabled: Boolean(orgId),
  });
  const slots = useQuery({
    queryKey: ["slot-templates", orgId, true],
    queryFn: () => (orgId ? listSlotTemplates(orgId, true) : Promise.resolve([])),
    enabled: Boolean(orgId),
  });
  const modes = useQuery({
    queryKey: ["exercise-modes-active"],
    queryFn: () => listExerciseModes(true),
  });

  const slotById = useMemo(() => {
    const m = new Map<string, SlotTemplate>();
    for (const s of slots.data ?? []) m.set(s.id, s);
    return m;
  }, [slots.data]);
  const modeById = useMemo(() => {
    const m = new Map<string, ExerciseMode>();
    for (const e of modes.data ?? []) m.set(e.id, e);
    return m;
  }, [modes.data]);

  const [showForm, setShowForm] = useState(false);
  const [viewing, setViewing] = useState<OpenShift | null>(null);

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => setShowForm(true)}
          disabled={!orgId}
        >
          {t("exchange.create")}
        </Button>
      </Stack>

      {shifts.isLoading ? (
        <CircularProgress />
      ) : (shifts.data ?? []).length === 0 ? (
        <Alert severity="info">{t("exchange.noShifts")}</Alert>
      ) : (
        <Stack spacing={1.5}>
          {(shifts.data ?? []).map((s) => (
            <ShiftCard
              key={s.id}
              shift={s}
              slot={slotById.get(s.slot_template_id)}
              mode={modeById.get(s.exercise_mode_id)}
              action={
                <Stack direction="row" spacing={1}>
                  <Tooltip title={t("exchange.viewApplications")}>
                    <IconButton onClick={() => setViewing(s)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  {s.status === "DRAFT" && (
                    <Tooltip title={t("exchange.publish")}>
                      <IconButton
                        color="primary"
                        onClick={() => {
                          publishOpenShift(s.id).then(() =>
                            qc.invalidateQueries({ queryKey: ["open-shifts-mgmt", orgId] }),
                          );
                        }}
                      >
                        <PublishIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {s.status === "PUBLISHED" && (
                    <Tooltip title={t("exchange.cancel")}>
                      <IconButton
                        color="warning"
                        onClick={() => {
                          cancelOpenShift(s.id).then(() =>
                            qc.invalidateQueries({ queryKey: ["open-shifts-mgmt", orgId] }),
                          );
                        }}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={t("exchange.delete")}>
                    <IconButton
                      color="error"
                      onClick={() => {
                        if (confirm("Supprimer ce créneau ?")) {
                          deleteOpenShift(s.id).then(() =>
                            qc.invalidateQueries({ queryKey: ["open-shifts-mgmt", orgId] }),
                          );
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
            />
          ))}
        </Stack>
      )}

      <CreateShiftDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        organizationId={orgId}
        slots={slots.data ?? []}
        modes={modes.data ?? []}
        onCreated={() => {
          setShowForm(false);
          qc.invalidateQueries({ queryKey: ["open-shifts-mgmt", orgId] });
        }}
      />

      <ApplicationsDialog
        open={viewing !== null}
        shift={viewing}
        onClose={() => setViewing(null)}
      />
    </>
  );
}


function ShiftCard({
  shift: s,
  slot,
  mode,
  isMine,
  action,
}: {
  shift: OpenShift;
  slot?: SlotTemplate;
  mode?: ExerciseMode;
  isMine?: boolean;
  action?: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Card variant="outlined" sx={isMine ? { borderColor: "success.main" } : undefined}>
      <CardContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                {s.date}
              </Typography>
              {slot && (
                <Typography variant="body2" color="text.secondary">
                  · {slot.label} ({slot.start_time.slice(0, 5)}–{slot.end_time.slice(0, 5)})
                </Typography>
              )}
              {mode && (
                <Chip
                  size="small"
                  variant="outlined"
                  sx={mode.color ? { borderColor: mode.color, color: mode.color } : undefined}
                  label={mode.code}
                />
              )}
              <Chip
                size="small"
                color={SHIFT_STATUS_COLOR[s.status]}
                label={t(`exchange.shiftStatus.${s.status}`)}
              />
              <Chip
                size="small"
                variant="outlined"
                label={t("exchange.placesTotal", { taken: s.places_taken, total: s.total_places })}
              />
              {s.reward_bonus_points > 0 && (
                <Chip
                  size="small"
                  color="secondary"
                  label={t("exchange.rewardPoints", { n: s.reward_bonus_points })}
                />
              )}
            </Stack>
            {s.title && (
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
                {s.title}
              </Typography>
            )}
            {s.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {s.description}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
      </CardContent>
    </Card>
  );
}


function CreateShiftDialog({
  open,
  onClose,
  organizationId,
  slots,
  modes,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  slots: SlotTemplate[];
  modes: ExerciseMode[];
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [date, setDate] = useState("");
  const [slotId, setSlotId] = useState("");
  const [modeId, setModeId] = useState("");
  const [totalPlaces, setTotalPlaces] = useState(1);
  const [rewardPoints, setRewardPoints] = useState(0);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDate(new Date().toISOString().slice(0, 10));
      setSlotId("");
      setModeId(modes.find((m) => m.code === "PRES")?.id ?? modes[0]?.id ?? "");
      setTotalPlaces(1);
      setRewardPoints(0);
      setTitle("");
      setDescription("");
      setServerError(null);
    }
  }, [open, modes]);

  const mutation = useMutation({
    mutationFn: () =>
      createOpenShift({
        organization_id: organizationId,
        date,
        slot_template_id: slotId,
        exercise_mode_id: modeId,
        title: title || null,
        description: description || null,
        total_places: totalPlaces,
        reward_bonus_points: rewardPoints,
      }),
    onSuccess: () => onCreated(),
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      setServerError(typeof detail === "string" ? detail : (e as Error).message);
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("exchange.create")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {serverError && <Alert severity="error">{serverError}</Alert>}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label={t("exchange.date")}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel>{t("exchange.selectSlot")}</InputLabel>
              <Select
                label={t("exchange.selectSlot")}
                value={slotId}
                onChange={(e) => setSlotId(e.target.value)}
              >
                {slots.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.label} ({s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel>{t("exchange.selectMode")}</InputLabel>
              <Select
                label={t("exchange.selectMode")}
                value={modeId}
                onChange={(e) => setModeId(e.target.value)}
              >
                {modes.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.code} — {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t("exchange.totalPlaces")}
              type="number"
              value={totalPlaces}
              onChange={(e) => setTotalPlaces(Math.max(1, parseInt(e.target.value || "1", 10)))}
              size="small"
              inputProps={{ min: 1, max: 20 }}
              sx={{ width: 120 }}
            />
            <TextField
              label={t("exchange.rewardBonus")}
              type="number"
              value={rewardPoints}
              onChange={(e) => setRewardPoints(Math.max(0, parseInt(e.target.value || "0", 10)))}
              size="small"
              inputProps={{ min: 0, max: 1000 }}
              sx={{ width: 140 }}
            />
          </Stack>
          <TextField
            label={t("exchange.titleField")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label={t("exchange.descriptionField")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
            maxRows={5}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={!date || !slotId || !modeId || mutation.isPending}
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function ApplicationsDialog({
  open,
  shift,
  onClose,
}: {
  open: boolean;
  shift: OpenShift | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const applications = useQuery({
    queryKey: ["applications", shift?.id],
    queryFn: () => (shift ? listApplications(shift.id) : Promise.resolve([])),
    enabled: open && Boolean(shift),
  });

  const eligibility = useQuery({
    queryKey: ["eligible-practitioners", shift?.id],
    queryFn: () =>
      shift
        ? listEligiblePractitioners(shift.id)
        : Promise.resolve({ required_skill_ids: [], items: [] }),
    enabled: open && Boolean(shift),
  });

  const practitioners = useQuery({
    queryKey: ["practitioners-all"],
    queryFn: () => listPractitioners({ limit: 200 }),
    enabled: open,
  });

  const pById = new Map<string, Practitioner>();
  for (const p of practitioners.data?.items ?? []) pById.set(p.id, p);

  const acceptMutation = useMutation({
    mutationFn: ({ aId }: { aId: string }) => acceptApplication(shift!.id, aId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["applications", shift?.id] });
      qc.invalidateQueries({ queryKey: ["open-shifts-mgmt"] });
    },
  });
  const rejectMutation = useMutation({
    mutationFn: ({ aId }: { aId: string }) => rejectApplication(shift!.id, aId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications", shift?.id] }),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {t("exchange.viewApplications")}
        {shift && (
          <Typography variant="body2" color="text.secondary">
            {shift.date} · {shift.places_taken} / {shift.total_places}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {eligibility.data && (
          <Alert
            severity={eligibility.data.required_skill_ids.length > 0 ? "info" : "success"}
            sx={{ mb: 2 }}
            variant="outlined"
          >
            {eligibility.data.required_skill_ids.length > 0
              ? t("exchange.eligibleWithSkills", {
                  required: eligibility.data.required_skill_ids.length,
                  count: eligibility.data.items.length,
                })
              : t("exchange.eligibleNoSkills", { count: eligibility.data.items.length })}
          </Alert>
        )}
        {applications.isLoading ? (
          <CircularProgress />
        ) : (applications.data ?? []).length === 0 ? (
          <Alert severity="info">{t("exchange.noApplications")}</Alert>
        ) : (
          <List dense>
            {(applications.data ?? []).map((a) => (
              <ApplicationRow
                key={a.id}
                application={a}
                practitioner={pById.get(a.practitioner_id)}
                onAccept={() => acceptMutation.mutate({ aId: a.id })}
                onReject={() => rejectMutation.mutate({ aId: a.id })}
              />
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}


function ApplicationRow({
  application: a,
  practitioner,
  onAccept,
  onReject,
}: {
  application: OpenShiftApplication;
  practitioner?: Practitioner;
  onAccept: () => void;
  onReject: () => void;
}) {
  const { t } = useTranslation();
  return (
    <ListItem
      divider
      secondaryAction={
        a.status === "PROPOSED" ? (
          <Stack direction="row" spacing={1}>
            <Tooltip title={t("exchange.accept")}>
              <IconButton color="success" onClick={onAccept}>
                <CheckCircleIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("exchange.reject")}>
              <IconButton color="error" onClick={onReject}>
                <CancelIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : (
          <Chip
            size="small"
            color={APPLICATION_STATUS_COLOR[a.status]}
            label={t(`exchange.applicationStatus.${a.status}`)}
          />
        )
      }
    >
      <ListItemText
        primary={
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {practitioner?.display_name ?? a.practitioner_id.slice(0, 8)}
          </Typography>
        }
        secondary={a.cover_text ?? a.decision_reason ?? undefined}
      />
    </ListItem>
  );
}
